/* eslint-disable react/require-default-props */
import React, { FC, JSX, ReactNode, useCallback, useMemo } from 'react';
import {
  Button,
  Icons,
  Loading,
} from '@sede-enterprise/shell-ds-react-framework';
import JSONPretty from 'react-json-pretty';

import Loader from '../../../../UI/Loader';
import { Modal } from '../../../../UI/Modal';
import useGetFieldAuditLogs from '../../../../hooks/fieldNomsSwingContract/useGetFieldAuditLogs';
import {
  IFieldAuditLogs,
  IFieldType,
} from '../../../../types/FieldNomsSwingContract.type';
import { ModalFooter } from '../ZKeyMapping/style';
import { AuditDetailWrapper } from './style';
import Table from '../../FieldNomination/Table';

const { InfoCircle: IconCircleInfo, ChevronDown, ChevronRight } = Icons;

interface FieldAuditLogsProps {
  open: boolean;
  onClose: () => void;
  url: string;
  children: ReactNode;
  fieldType: IFieldType;
  modalTitle?: string;
}

const FieldAuditLogs: FC<FieldAuditLogsProps> = ({
  open,
  onClose,
  modalTitle = 'Swing Contract Info and Audit Logs',
  url,
  fieldType,
  children,
}) => {
  const {
    hasLoading,
    auditLogs,
    resetLogsOnCloseModal,
    onExpandLogs,
    onPayloadLogs,
  } = useGetFieldAuditLogs({
    url,
    hasLogs: open,
    fieldType,
  });

  const handleOnClose = useCallback(() => {
    resetLogsOnCloseModal();
    onClose();
  }, [resetLogsOnCloseModal, onClose]);

  const renderFooter = useMemo(
    (): JSX.Element => (
      <ModalFooter>
        <Button variant='outlined' onClick={handleOnClose}>
          Close
        </Button>
      </ModalFooter>
    ),
    [handleOnClose]
  );

  const renderMessage = (item: IFieldAuditLogs): JSX.Element => {
    return (
      <>
        <div>
          <span>{item.note}</span>
          {fieldType === 'SwingContract' && item?.payload && (
            <Button
              data-testid='info-button'
              size='small'
              className='logs-info'
              onClick={() => onPayloadLogs?.(item)}
              key={`info-btn-${item.id}`}
              icon={item.setaPayloadLoading ? <Loading /> : <IconCircleInfo />}
              iconOnly
            />
          )}
        </div>
        {item.expanded && (
          <div>
            {item.setaPayload && Object.keys(item.setaPayload).length > 0 && (
              <JSONPretty data={item.setaPayload} />
            )}
          </div>
        )}
      </>
    );
  };

  const renderExpandIcon = (item: IFieldAuditLogs): JSX.Element | null => {
    if (item?.setaPayload === null) {
      return null;
    }
    return (
      <Button
        iconOnly
        icon={item.expanded ? <ChevronDown /> : <ChevronRight />}
        size='small'
        variant='transparent'
        onClick={() => onExpandLogs(item.id)}
      />
    );
  };

  const renderDateTime = (item: IFieldAuditLogs): JSX.Element => {
    const formatted =
      item.createdDateTimeinGMT?.split('.')?.[0]?.replace('T', ' ') || '';
    return <div>{formatted}</div>;
  };

  const columns = [
    {
      accessorKey: 'expanded',
      header: '',
      cell: ({ row }) => renderExpandIcon(row?.original),
      size: 50,
      enableSorting: false,
    },
    {
      accessorKey: 'note',
      header: 'Message',
      cell: ({ row }) => renderMessage(row?.original),
      minSize: 300,
    },
    {
      accessorKey: 'createdDateTimeinGMT',
      header: 'Timestamp',
      size: 200,
      cell: ({ row }) => renderDateTime(row?.original),
      enableSorting: true,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={handleOnClose}
      title={modalTitle}
      isDraggable
      maskClosable={false}
      footer={renderFooter}
      width='50%'
    >
      <AuditDetailWrapper>
        {children}
        {hasLoading ? (
          <Loader />
        ) : (
          <Table
            columns={columns}
            data={auditLogs}
            size='small'
            pagination={false}
            maxHeight={600}
          />
        )}
      </AuditDetailWrapper>
    </Modal>
  );
};

export default FieldAuditLogs;
